import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import "./BookDemoConfirm.css";

const TEAM_SIZES = ["1-10", "11-50", "51-200", "201-500", "500+"];
const ROLES = ["CEO", "CTO", "Manager", "Team Lead", "Developer", "Other"];

const formatDemoDate = (date) => {
  if (!date || !(date instanceof Date)) return { dateStr: "", timeStr: "10:00 AM" };
  const dateStr = date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  return { dateStr, timeStr: "10:00 AM" };
};

const BookDemoConfirm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [fullName, setFullName] = useState("John Doe");
  const [workEmail, setWorkEmail] = useState("john@company.com");
  const [companyName, setCompanyName] = useState("Acme Inc.");
  const [teamSize, setTeamSize] = useState("");
  const [role, setRole] = useState("");
  const [notes, setNotes] = useState("");

  const selectedDate = location.state?.date;
  const { dateStr, timeStr } = formatDemoDate(selectedDate);

  useEffect(() => {
    if (!selectedDate && location.key !== "default") {
      navigate("/book-demo", { replace: true });
    }
  }, [selectedDate, navigate, location.key]);

  const handleBack = () => navigate("/book-demo");
  const handleChange = () => navigate("/book-demo");
  const handleSubmit = (e) => {
    e.preventDefault();
    navigate("/thanks-contact-us", {
      state: { date: selectedDate, email: workEmail },
    });
  };

  return (
    <>
      <Helmet>
        <title>Confirm Your Demo | TeamGrid</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="book-demo-confirm-page">
      <PublicHeader />

      <section className="book-demo-confirm-section">
        <div className="book-demo-confirm-inner">
          <div className="book-demo-confirm-head">
            <h1 className="book-demo-confirm-title">Almost There!</h1>
            <p className="book-demo-confirm-subtitle">
              Just a few details to confirm your demo booking
            </p>
          </div>

          <div className="book-demo-confirm-card">
            <div className="book-demo-confirm-scheduled">
              <div className="book-demo-confirm-scheduled-text">
                <p className="book-demo-confirm-scheduled-label">Your demo is scheduled for:</p>
                <p className="book-demo-confirm-scheduled-date">{dateStr || "Select a date"}</p>
                <p className="book-demo-confirm-scheduled-time">{timeStr}</p>
              </div>
              <button type="button" onClick={handleChange} className="book-demo-confirm-change">
                Change
              </button>
            </div>

            <form onSubmit={handleSubmit} className="book-demo-confirm-form">
              <div className="book-demo-confirm-field">
                <label htmlFor="fullName">Full Name *</label>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="book-demo-confirm-input"
                />
              </div>
              <div className="book-demo-confirm-field">
                <label htmlFor="workEmail">Work Email *</label>
                <input
                  id="workEmail"
                  type="email"
                  value={workEmail}
                  onChange={(e) => setWorkEmail(e.target.value)}
                  required
                  className="book-demo-confirm-input"
                />
              </div>
              <div className="book-demo-confirm-field">
                <label htmlFor="companyName">Company Name *</label>
                <input
                  id="companyName"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  className="book-demo-confirm-input"
                />
              </div>
              <div className="book-demo-confirm-row">
                <div className="book-demo-confirm-field">
                  <label htmlFor="teamSize">Team Size *</label>
                  <select
                    id="teamSize"
                    value={teamSize}
                    onChange={(e) => setTeamSize(e.target.value)}
                    required
                    className="book-demo-confirm-select"
                  >
                    <option value="">Select</option>
                    {TEAM_SIZES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="book-demo-confirm-field">
                  <label htmlFor="role">Your Role *</label>
                  <select
                    id="role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    required
                    className="book-demo-confirm-select"
                  >
                    <option value="">Select</option>
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="book-demo-confirm-field">
                <label htmlFor="notes">Anything specific you'd like to discuss? (Optional)</label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g., integration requirements, specific use cases, team structure..."
                  className="book-demo-confirm-textarea"
                  rows={4}
                />
              </div>
              <div className="book-demo-confirm-actions">
                <button type="button" onClick={handleBack} className="book-demo-confirm-btn-back">
                  Back
                </button>
                <button type="submit" className="book-demo-confirm-btn-confirm">
                  Confirm Booking
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <footer
        className="book-demo-confirm-footer"
        style={{ borderTopColor: "rgba(0, 0, 0, 0.2)", fontFamily: "Inter, sans-serif" }}
      >
        <div className="book-demo-confirm-footer-inner">
          <a href="/security" className="book-demo-confirm-footer-link">Security</a>
          <a href="/privacy-policy" className="book-demo-confirm-footer-link">Privacy</a>
          <a href="/terms" className="book-demo-confirm-footer-link">Terms</a>
          <a href="/contact-us" className="book-demo-confirm-footer-link">Contact</a>
          <span className="book-demo-confirm-footer-copy">© 2026 TeamGrid</span>
        </div>
      </footer>
    </div>
    </>
  );
};

export default BookDemoConfirm;
